import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';

// Standard nav links shown on every public page
const NAV_LINKS = [
  { label: 'Features',   href: '/#features' },
  { label: 'Pricing',    href: '/#pricing' },
  { label: 'Free Trial', href: '/#trial' },
  { label: 'Demo',       href: '/#demo' },
  { label: 'Mobile App', href: '/mobile' },
];

/**
 * Shared public-facing header used on all marketing / public pages.
 *
 * Props:
 *   badge        — optional element rendered next to the logo (e.g. "INTERACTIVE DEMO" chip)
 *   extraButtons — optional element rendered left of the Sign In / Dashboard buttons
 */
const PublicHeader = ({ badge, extraButtons }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Only highlight page links (not anchor links — they are scroll targets, not pages)
  const isActive = (href) => {
    if (href.startsWith('/#')) return false;
    return pathname === href;
  };

  // Scroll to top and close mobile menu when navigating to a new page
  const handleNavClick = () => {
    window.scrollTo(0, 0);
    setMobileOpen(false);
  };

  // Close mobile menu on anchor link click
  const handleAnchorClick = () => setMobileOpen(false);

  return (
    <header className="border-b border-[#E5E7EB] sticky top-0 bg-white/90 backdrop-blur-md z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">

          {/* ── Logo ─────────────────────────────────────────────────────── */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" onClick={handleNavClick} data-testid="public-header-logo">
              <img src="/logo.png" alt="KStreak" className="w-8 h-8 rounded-lg object-contain" />
              <span className="font-semibold text-[#0A0A0A]">KStreak</span>
            </Link>
            {badge && badge}
          </div>

          {/* ── Nav links (desktop only) ──────────────────────────────────── */}
          <nav className="hidden md:flex items-center gap-5 text-sm">
            {NAV_LINKS.map(({ label, href }) =>
              href.startsWith('/#') ? (
                <a
                  key={label}
                  href={href}
                  className="text-[#4B5563] hover:text-[#002FA7] transition-colors"
                >
                  {label}
                </a>
              ) : (
                <Link
                  key={label}
                  to={href}
                  onClick={handleNavClick}
                  className={`transition-colors hover:text-[#002FA7] ${
                    isActive(href) ? 'text-[#002FA7] font-medium' : 'text-[#4B5563]'
                  }`}
                >
                  {label}
                </Link>
              )
            )}
          </nav>

          {/* ── Right-side buttons ───────────────────────────────────────── */}
          <div className="flex items-center gap-3">
            {extraButtons && extraButtons}
            {user ? (
              <Link to="/dashboard">
                <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="public-header-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden md:inline-flex">
                  <Button variant="ghost" size="sm" data-testid="public-header-signin-btn">Sign In</Button>
                </Link>
                <Link to="/register" className="hidden md:inline-flex">
                  <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="public-header-trial-btn">
                    Start Free Trial
                  </Button>
                </Link>
              </>
            )}

            {/* ── Hamburger button (mobile only) ───────────────────────── */}
            <button
              className="md:hidden p-2 rounded-md text-[#4B5563] hover:text-[#002FA7] hover:bg-gray-100 transition-colors"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

        </div>
      </div>

      {/* ── Mobile dropdown menu ─────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white/95 backdrop-blur-md">
          <nav className="flex flex-col px-4 py-3 gap-1">
            {NAV_LINKS.map(({ label, href }) =>
              href.startsWith('/#') ? (
                <a
                  key={label}
                  href={href}
                  onClick={handleAnchorClick}
                  className="py-2.5 px-3 rounded-md text-sm text-[#4B5563] hover:text-[#002FA7] hover:bg-gray-50 transition-colors"
                >
                  {label}
                </a>
              ) : (
                <Link
                  key={label}
                  to={href}
                  onClick={handleNavClick}
                  className={`py-2.5 px-3 rounded-md text-sm transition-colors hover:bg-gray-50 hover:text-[#002FA7] ${
                    isActive(href) ? 'text-[#002FA7] font-medium bg-blue-50' : 'text-[#4B5563]'
                  }`}
                >
                  {label}
                </Link>
              )
            )}

            {/* Auth buttons inside mobile menu */}
            <div className="border-t border-[#E5E7EB] mt-2 pt-3 flex flex-col gap-2">
              {user ? (
                <Link to="/dashboard" onClick={handleNavClick}>
                  <Button size="sm" className="w-full bg-[#002FA7] hover:bg-[#00227A]">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={handleNavClick}>
                    <Button variant="outline" size="sm" className="w-full">Sign In</Button>
                  </Link>
                  <Link to="/register" onClick={handleNavClick}>
                    <Button size="sm" className="w-full bg-[#002FA7] hover:bg-[#00227A]">Start Free Trial</Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
